/**
 * Field-event props on the infield: the long jump runway and pit, and the high jump
 * approach, uprights, bar and mats. Both run along +X like the track so the same cameras
 * and the same athlete animation work everywhere.
 */
import * as THREE from 'three';
import { HIGH_JUMP, LONG_JUMP, PV_PLANT_BEFORE_BAR, PV_PLANT_TOLERANCE } from '@decathlon/shared';
import { box, mat } from '../blocky.js';

/** Lane centre (z) of each infield runway. */
export const LJ_Z = -7;
export const HJ_Z = -24;
const RUNWAY = '#c1493f';
const SAND = '#f3d9a0';
const LINE = '#f5f5f5';

export class LongJumpArea {
  readonly group = new THREE.Group();
  private flag: THREE.Group;

  constructor() {
    const g = this.group;
    const board = LONG_JUMP.board;
    // Runway from behind the start to the board, then the sand pit.
    g.add(box(board + 8, 0.08, 1.4, RUNWAY, board / 2 - 4, 0.0, LJ_Z, 0.02));
    g.add(box(0.2, 0.09, 1.4, LINE, board, 0.01, LJ_Z, 0)); // take-off board
    g.add(box(0.06, 0.1, 1.4, '#e53935', board + 0.13, 0.02, LJ_Z, 0)); // plasticine foul strip
    const pit = box(
      LONG_JUMP.pitLength + 1,
      0.12,
      3.2,
      SAND,
      board + (LONG_JUMP.pitLength + 1) / 2,
      -0.01,
      LJ_Z,
      0.03,
    );
    pit.receiveShadow = true;
    g.add(pit);
    // Kerb around the pit.
    g.add(
      box(
        LONG_JUMP.pitLength + 1.2,
        0.16,
        0.12,
        LINE,
        board + (LONG_JUMP.pitLength + 1) / 2,
        0.06,
        LJ_Z - 1.66,
        0.02,
      ),
    );
    g.add(
      box(
        LONG_JUMP.pitLength + 1.2,
        0.16,
        0.12,
        LINE,
        board + (LONG_JUMP.pitLength + 1) / 2,
        0.06,
        LJ_Z + 1.66,
        0.02,
      ),
    );
    // Distance markers every metre beside the pit.
    for (let m = 1; m <= LONG_JUMP.pitLength; m++) {
      g.add(box(0.05, 0.02, 0.3, m % 5 === 0 ? '#1e88e5' : LINE, board + m, 0.07, LJ_Z - 1.9, 0));
    }
    // Landing flag, hidden until a jump lands.
    this.flag = new THREE.Group();
    this.flag.add(box(0.06, 1.2, 0.06, LINE, 0, 0.6, 0, 0.01));
    this.flag.add(box(0.5, 0.3, 0.04, '#e53935', 0.25, 1.05, 0, 0.01));
    this.flag.visible = false;
    g.add(this.flag);
  }

  /** Plant the flag where the jump landed (x along the runway). */
  mark(x: number | null): void {
    this.flag.visible = x !== null;
    if (x !== null) this.flag.position.set(x, 0, LJ_Z - 1.4);
  }
}

export class HighJumpArea {
  readonly group = new THREE.Group();
  private bar: THREE.Mesh;
  private barPivot = new THREE.Group();
  private height = 1.8;
  private falling = false;
  private fallT = 0;
  private vault = false;
  private pole = box(0.06, 5, 0.06, '#fdd835', 0, 2.5, HJ_Z, 0);
  private uprights: THREE.Mesh[] = [];
  /** Pole vault: the strip of runway where the plant is accepted. */
  private plantZone: THREE.Mesh;
  /** Pole vault: the recessed box the pole tip lands in. */
  private plantBox = new THREE.Group();

  constructor() {
    const g = this.group;
    const bx = HIGH_JUMP.bar;
    this.pole.visible = false;
    g.add(this.pole);
    this.plantZone = new THREE.Mesh(
      new THREE.BoxGeometry(PV_PLANT_TOLERANCE * 2, 0.03, 1.7),
      new THREE.MeshLambertMaterial({ color: '#43a047', transparent: true, opacity: 0.55 }),
    );
    this.plantZone.position.set(bx - PV_PLANT_BEFORE_BAR, 0.1, HJ_Z);
    this.plantZone.visible = false;
    g.add(this.plantZone);
    // Planting box: a sloped recess in front of the uprights with a white rim, so the
    // player can see where the pole tip has to land. Only shown for the vault.
    const boxX = bx - 0.4;
    const floor = box(1.0, 0.03, 0.6, '#1b2430', boxX - 0.3, 0.05, HJ_Z, 0);
    const ramp = box(0.9, 0.03, 0.56, '#37474f', boxX - 0.35, 0.09, HJ_Z, 0);
    ramp.rotation.z = -0.12;
    const backWall = box(0.06, 0.22, 0.6, '#546e7a', boxX + 0.2, 0.17, HJ_Z, 0.01);
    const rimL = box(1.1, 0.05, 0.06, LINE, boxX - 0.3, 0.12, HJ_Z - 0.32, 0.01);
    const rimR = box(1.1, 0.05, 0.06, LINE, boxX - 0.3, 0.12, HJ_Z + 0.32, 0.01);
    const rimF = box(0.06, 0.05, 0.7, LINE, boxX - 0.85, 0.12, HJ_Z, 0.01);
    this.plantBox.add(floor, ramp, backWall, rimL, rimR, rimF);
    this.plantBox.visible = false;
    g.add(this.plantBox);
    // Approach: a fan-shaped apron reads as a curved run-up; keep it a straight strip here.
    g.add(box(bx + 8, 0.08, 1.6, RUNWAY, bx / 2 - 4, 0.0, HJ_Z, 0.02));
    // Mats behind the bar.
    const mats = box(
      HIGH_JUMP.matDepth,
      HIGH_JUMP.matHeight,
      5,
      '#1e88e5',
      bx + 0.3 + HIGH_JUMP.matDepth / 2,
      HIGH_JUMP.matHeight / 2,
      HJ_Z,
      0.12,
    );
    g.add(mats);
    g.add(
      box(
        HIGH_JUMP.matDepth + 0.2,
        0.08,
        5.2,
        '#1565c0',
        bx + 0.3 + HIGH_JUMP.matDepth / 2,
        HIGH_JUMP.matHeight + 0.02,
        HJ_Z,
        0.03,
      ),
    );
    // Uprights.
    for (const side of [-1, 1]) {
      const upright = box(0.14, 2.9, 0.14, LINE, bx, 1.45, HJ_Z + side * 2.2, 0.02);
      this.uprights.push(upright);
      g.add(upright);
      g.add(box(0.6, 0.1, 0.6, '#546e7a', bx, 0.05, HJ_Z + side * 2.2, 0.02));
    }
    // Bar: a pivot at one upright so it can swing off.
    this.barPivot.position.set(bx, this.height, HJ_Z - 2.2);
    this.bar = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 4.3, 8), mat('#fdd835'));
    this.bar.rotation.x = Math.PI / 2;
    this.bar.position.z = 2.15;
    this.bar.castShadow = true;
    this.barPivot.add(this.bar);
    g.add(this.barPivot);
    // Height indicator boards on the upright.
    g.add(box(0.5, 0.3, 0.05, '#1b2430', bx, 2.95, HJ_Z + 2.2, 0.01));
  }

  setVault(on: boolean): void {
    this.vault = on;
    this.pole.visible = on;
    this.plantZone.visible = on;
    this.plantBox.visible = on;
    for (const u of this.uprights) {
      u.scale.y = on ? 2.4 : 1;
      u.position.y = 1.45 * u.scale.y;
    }
  }
  setPole(x: number, y: number, planted: boolean): void {
    this.pole.visible = this.vault && (planted || y < 1.5);
    // `y` is the centre-of-mass height (about 1 m when running), so the hands sit a little
    // above it: chest height, ahead of and beside the body, never through the head.
    const a = new THREE.Vector3(x + 0.35, y + 0.15, HJ_Z + 0.3);
    const boxX = HIGH_JUMP.bar - 0.4;
    // On the approach the tip comes down from the carry and reaches for the box: fully
    // lowered by the near edge of the plant zone, so the player can see the plant coming.
    const lower = Math.min(1, Math.max(0, (x - (boxX - 10)) / (10 - PV_PLANT_BEFORE_BAR - PV_PLANT_TOLERANCE)));
    const carry = new THREE.Vector3(x + 4.3, y + 1.7, HJ_Z + 0.3);
    const reach = new THREE.Vector3(Math.max(x + 4.3, boxX), 0.25, HJ_Z + 0.1);
    const b = planted ? new THREE.Vector3(boxX, 0.1, HJ_Z) : carry.lerp(reach, lower * lower);
    this.pole.position.copy(a).add(b).multiplyScalar(0.5);
    this.pole.scale.y = a.distanceTo(b) / 5;
    this.pole.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), b.sub(a).normalize());
  }

  setBar(height: number): void {
    this.height = height;
    this.falling = false;
    this.fallT = 0;
    this.barPivot.position.y = height;
    this.barPivot.rotation.set(0, 0, 0);
    this.barPivot.position.x = HIGH_JUMP.bar;
  }

  /** Re-seat the bar at a height only if it changed (and it is not mid-fall). */
  setBarHeight(height: number): void {
    if (this.falling || Math.abs(height - this.height) < 0.001) return;
    this.setBar(height);
  }

  /** Knock the bar off the pegs. */
  knock(): void {
    if (this.falling) return;
    this.falling = true;
    this.fallT = 0;
  }

  update(dt: number): void {
    if (!this.falling) return;
    this.fallT += dt;
    const t = this.fallT;
    // Swing down from the far peg and drop to the mat.
    this.barPivot.rotation.x = -Math.min(1.2, t * 2.2);
    this.barPivot.position.y = Math.max(HIGH_JUMP.matHeight + 0.05, this.height - 4.9 * t * t);
    this.barPivot.position.x = HIGH_JUMP.bar + Math.min(0.8, t * 1.5);
  }
}
